import React, { useCallback } from 'react';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';

import { useToast } from 'core/components/toast';
import { Button } from 'core/components/button';
import { useStore } from 'core/store';
import useSession from 'core/hooks/use-session';
import { track } from 'core/helpers/analytics';

import useStyles from './verify-property-container.styles';

const VerifyPropertyFooter = observer(({ setShowCannotOfferModal }) => {
  const classes = useStyles();

  const { handleSubmit, setErrors, setTouched, setFieldValue, validateForm, setFieldTouched, values } =
    useFormikContext();
  const toast = useToast();

  const {
    account: { sendEventAsMember },
    offer
  } = useStore();
  const { user, isAgency } = useSession();

  const onContinueToCheckout = useCallback(async () => {
    // run validation before submit so that we have the updated errors
    const updateErrors = await validateForm();
    if (Object.keys(updateErrors).length > 0) {
      setErrors(updateErrors);

      // Convert errors object to touched object with boolean true values
      const touchedFields = Object.keys(updateErrors).reduce((acc, key) => {
        if (typeof updateErrors[key] === 'object') {
          acc[key] = Object.keys(updateErrors[key]).reduce((nested, nestedKey) => {
            nested[nestedKey] = true;
            return nested;
          }, {});
        } else {
          acc[key] = true;
        }
        return acc;
      }, {});

      setTouched(touchedFields);

      if (updateErrors?.signedPropertyUWAttestation) {
        toast.notify({
          type: 'error',
          message: 'You must sign off that you verified the property before continuing'
        });
      } else {
        const missingAField = Object.values(updateErrors).some((e) =>
          Object.values(e).some((err) => err.includes('required'))
        );

        if (missingAField) {
          toast.notify({
            type: 'error',
            message: 'Please complete the form to continue.'
          });
        } else {
          // if not missing a required field, then errors at this point are for a wrong answer, meaning offer would be ineligible for home/auto
          await sendEventAsMember({
            eventName: 'Underwriting review page submitted - Applicant',
            userId: offer?.offer?.webUserId,
            properties: JSON.stringify({ isAgency, eligible: false })
          });
          track('Staff User: Underwriting Review Page Submitted', {
            offerId: offer?.offer?.id,
            webUserId: offer?.offer?.webUserId,
            agent: user?.username,
            isAgency,
            eligible: false
          });
          setShowCannotOfferModal(true);
        }
      }
    } else {
      await sendEventAsMember({
        eventName: 'Underwriting review page submitted - Applicant',
        userId: offer?.offer?.webUserId,
        properties: JSON.stringify({ isAgency, eligible: true })
      });
      track('Staff User: Underwriting Review Page Submitted', {
        offerId: offer?.offer?.id,
        webUserId: offer?.offer?.webUserId,
        agent: user?.username,
        isAgency,
        eligible: true
      });
      await handleSubmit();
    }
  }, [
    validateForm,
    setErrors,
    setTouched,
    handleSubmit,
    setShowCannotOfferModal,
    toast,
    sendEventAsMember,
    offer,
    isAgency,
    user
  ]);

  const onClick = async () => {
    if (Object.keys(values.home).length) {
      await setFieldValue('home.signedPropertyUWAttestation', values.signedPropertyUWAttestation);
      await setFieldTouched('home.signedPropertyUWAttestation');
    }
    if (Object.keys(values.auto).length) {
      await setFieldValue('auto.signedPropertyUWAttestation', values.signedPropertyUWAttestation);
      await setFieldTouched('auto.signedPropertyUWAttestation');
    }
    await onContinueToCheckout();
  };

  return (
    <Grid className={classes.floatingContainer}>
      <Grid className={classes.footerContainer}>
        <Button variant="contained" color="primary" className={classes.footerBtn} onClick={onClick}>
          Continue
        </Button>
      </Grid>
    </Grid>
  );
});

export default VerifyPropertyFooter;
